import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select as MuiSelect,
  Tooltip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { DoubleYAxisLegendSeriesOption } from '~/shared/utils/doubleYAxisLegend';
import { shouldShowLineChartLegendTooltip } from '~/shared/utils/lineChartLegendTooltip';
import { formatTooltipHtml } from '~/shared/utils/tooltipHtml';

export type SeriesOption = DoubleYAxisLegendSeriesOption;

export type { DoubleYAxisLegendAxisConfig } from '~/shared/utils/doubleYAxisLegend';
export { buildDoubleYAxisLegendOptions } from '~/shared/utils/doubleYAxisLegend';

export type SeriesControlsProps = {
  className?: string;
  compact?: boolean;
  align?: 'flex-start' | 'flex-end' | 'center';
  showMeanToggle?: boolean;
  meanLabel?: string;
  meanChecked?: boolean;
  meanDisabled?: boolean;
  onMeanChange?: (checked: boolean) => void;
  topRowExtra?:
    | React.ReactNode
    | {
        type: 'sma';
        selectProps: {
          options: number[];
          value: number;
          onChange: (value: number) => void;
        };
      };
  seriesOptions?: SeriesOption[];
  selectedSeries?: string[] | null;
  onSeriesChange?: (selected: string[]) => void;
  seriesDisabled?: boolean;
  seriesLabel?: string;
  maxSelectedLabelCount?: number;
  menuWidth?: number;
  emptySelectionLabel?: string;
  allSelectionLabel?: string;
  invertMenuOrder?: boolean;
  seriesTooltipCfg?: any;
  defaultPartialSelection?: string[];
};

const Container = styled.div<{ $align: string }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $align }) => $align};
  gap: 6px;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

const TopExtraWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SeriesSelectLabel = styled.span`
  display: flex;
  align-items: center;
  font-weight: 400;
  font-size: 13px;
  height: 24px;
`;

const SeriesSelectControl = styled(FormControl)<{ $width: number }>`
  min-width: ${({ $width }) => $width}px !important;
  max-width: ${({ $width }) => $width}px !important;
  height: 34px !important;

  .MuiOutlinedInput-notchedOutline {
    border-radius: 5px;
    border: 1px solid var(--filter-bar-select-border);
  }

  .MuiOutlinedInput-root {
    background: var(--background-chart);
    height: 34px;
  }

  .MuiOutlinedInput-input {
    padding-top: 6px;
    padding-bottom: 6px;
    font-size: 15px;
    font-weight: 400;
    color: #33485e;
  }

  .MuiSelect-select {
    padding-top: 4px !important;
    padding-bottom: 4px !important;
    background: transparent;
    font-size: 15px;
    font-weight: 400;
    color: #33485e;
  }
`;

const MeanToggleLabel = styled.label<{ $issolo: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 30px;
  margin-right: ${({ $issolo }) => ($issolo ? 0 : 12)}px;
  margin-bottom: 0;
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 0.4px;
  color: var(--color-chart-title, #33485e);
  cursor: pointer;
  user-select: none;

  span {
    display: inline-flex;
    align-items: center;
  }

  &[data-disabled='1'] {
    opacity: 0.6;
    cursor: default;
  }
`;

const MeanToggleInput = styled.input`
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

const LEGEND_SEPARATOR_PREFIX = '__legend-separator__';

const isLegendSeparatorValue = (value: string) => value.startsWith(LEGEND_SEPARATOR_PREFIX);

const borderDefault = '1px solid var(--filter-bar-select-border)';
const borderActive = '1px solid var(--filter-bar-select-border-active)';

const normalizeSelection = (values: unknown, orderedNames: string[]): string[] => {
  if (Array.isArray(values)) {
    const requested = new Set(
      values.filter((value): value is string => typeof value === 'string' && !isLegendSeparatorValue(value)),
    );
    return orderedNames.filter(name => requested.has(name));
  }
  if (typeof values === 'string') {
    const requested = new Set(values.split(',').filter(value => !isLegendSeparatorValue(value)));
    return orderedNames.filter(name => requested.has(name));
  }
  return orderedNames;
};

const SeriesControls: React.FC<SeriesControlsProps> = ({
  className,
  compact = false,
  align = 'flex-end',
  showMeanToggle = false,
  meanLabel = 'Mean',
  meanChecked = false,
  meanDisabled = false,
  onMeanChange,
  topRowExtra,
  seriesOptions: rawSeriesOptions,
  selectedSeries,
  onSeriesChange,
  seriesDisabled = false,
  seriesLabel = 'Series:',
  maxSelectedLabelCount = 2,
  menuWidth,
  emptySelectionLabel = 'No series',
  allSelectionLabel = 'All series',
  invertMenuOrder = true,
  seriesTooltipCfg,
  defaultPartialSelection,
}) => {
  const menuSize = menuWidth ?? (compact ? 180 : 220);
  const seriesOptions = useMemo(() => rawSeriesOptions ?? [], [rawSeriesOptions]);
  const menuSeriesOptions = useMemo(
    () => (invertMenuOrder ? [...seriesOptions].reverse() : seriesOptions),
    [invertMenuOrder, seriesOptions],
  );
  const orderedNames = useMemo(() => seriesOptions.map(option => option.name), [seriesOptions]);
  const resolvedSelection = useMemo(() => {
    if (!orderedNames.length) return [];
    if (selectedSeries === null || selectedSeries === undefined) {
      if (defaultPartialSelection?.length) {
        const partialSet = new Set(defaultPartialSelection);
        return orderedNames.filter(name => partialSet.has(name));
      }
      return orderedNames;
    }
    if (selectedSeries.length === 0) {
      return [];
    }
    const selectionSet = new Set(selectedSeries);
    return orderedNames.filter(name => selectionSet.has(name));
  }, [orderedNames, selectedSeries, defaultPartialSelection]);

  const [isSeriesOpen, setIsSeriesOpen] = useState(false);

  const handleSeriesSelectChange = (event: SelectChangeEvent<unknown>) => {
    const normalized = normalizeSelection(event.target.value, orderedNames);
    onSeriesChange?.(normalized);
  };

  const handleSeriesMenuItemClick = useCallback(
    (seriesName: string, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const nextSet = new Set(resolvedSelection);
      if (nextSet.has(seriesName)) {
        nextSet.delete(seriesName);
      } else {
        nextSet.add(seriesName);
      }

      onSeriesChange?.(orderedNames.filter(name => nextSet.has(name)));
    },
    [onSeriesChange, orderedNames, resolvedSelection],
  );

  const handleMeanToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (meanDisabled && event.target.checked) {
      return;
    }
    onMeanChange?.(event.target.checked);
  };

  const renderSelectedValue = (value: unknown) => {
    const selection = normalizeSelection(value, orderedNames);
    if (!selection.length) {
      return emptySelectionLabel;
    }
    if (selection.length === orderedNames.length) {
      return allSelectionLabel;
    }
    if (selection.length <= maxSelectedLabelCount) {
      return selection.join(', ');
    }
    return `${selection.length} selected`;
  };

  const showTopRowMean = showMeanToggle && !compact;
  const showBottomRowMean = showMeanToggle && compact;

  const renderSeriesOptionTooltip = (option: SeriesOption, content: React.ReactElement) => {
    if (!seriesTooltipCfg || !shouldShowLineChartLegendTooltip(option.name, option.name, option.description)) {
      return content;
    }

    const tooltipHtml = formatTooltipHtml({
      title: option.name,
      description: option.description,
      tooltipCfg: seriesTooltipCfg,
      letterSpacingAdjustment: -0.75,
    });

    return (
      <Tooltip
        placement="left"
        arrow
        describeChild
        slotProps={{
          popper: {
            sx: { zIndex: 1400 },
          },
        }}
        title={<span dangerouslySetInnerHTML={{ __html: tooltipHtml }} />}
        disableHoverListener={false}
      >
        <span className="series-controls__tooltip-target" style={{ display: 'inline-flex', flex: 1, minWidth: 0 }}>
          {content}
        </span>
      </Tooltip>
    );
  };

  const renderTopRowExtra = () => {
    if (!topRowExtra) return null;
    if (React.isValidElement(topRowExtra) || typeof topRowExtra !== 'object') {
      return <TopExtraWrapper className="series-controls__top-extra">{topRowExtra}</TopExtraWrapper>;
    }
    return null;
  };

  return (
    <Container className={className} $align={align}>
      {(showTopRowMean || topRowExtra) && (
        <ControlsRow className="sma-row">
          {showTopRowMean && (
            <MeanToggleLabel $issolo={!topRowExtra} data-disabled={meanDisabled ? 1 : 0}>
              <span>{meanLabel}</span>
              <MeanToggleInput
                type="checkbox"
                checked={meanChecked}
                onChange={handleMeanToggle}
                disabled={meanDisabled}
              />
            </MeanToggleLabel>
          )}
          {renderTopRowExtra()}
        </ControlsRow>
      )}
      {seriesOptions.length > 0 && (
        <ControlsRow className="series-row">
          {!compact && <SeriesSelectLabel className="series-controls__label">{seriesLabel}</SeriesSelectLabel>}
          <SeriesSelectControl $width={menuSize} size="small">
            <MuiSelect
              multiple
              value={resolvedSelection}
              onChange={handleSeriesSelectChange}
              onOpen={() => {
                if (!seriesDisabled) {
                  setIsSeriesOpen(true);
                }
              }}
              onClose={() => setIsSeriesOpen(false)}
              input={<OutlinedInput />}
              renderValue={renderSelectedValue}
              disabled={seriesDisabled}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 320,
                    backgroundColor: 'var(--background-chart)',
                    border: borderActive,
                    borderTop: 'none',
                    marginTop: '-2px',
                    borderRadius: '5px',
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    width: menuSize,
                    boxShadow: 'none',
                  },
                },
                MenuListProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      fontSize: '15px',
                      color: '#33485e',
                    },
                    '& .series-controls-legend-divider': {
                      width: '100%',
                      minHeight: '16px',
                      height: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 !important',
                      margin: '0 !important',
                      opacity: '1 !important',
                      backgroundColor: 'transparent !important',
                      cursor: 'default',
                    },
                    '& .series-controls-legend-divider-line': {
                      width: '100%',
                      height: '1px',
                      backgroundColor: '#99a3af',
                    },
                  },
                },
              }}
              sx={{
                padding: '4px',
                '& .MuiSelect-icon': {
                  right: 3,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: isSeriesOpen ? borderActive : borderDefault,
                  borderBottom: isSeriesOpen ? 'none' : undefined,
                  borderRadius: '5px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: borderActive,
                  borderBottom: isSeriesOpen ? 'none' : undefined,
                  borderRadius: '5px',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: isSeriesOpen ? borderActive : borderDefault,
                  borderBottom: isSeriesOpen ? 'none' : undefined,
                  borderRadius: '5px',
                },
              }}
            >
              {menuSeriesOptions.map(option => {
                const isChecked = resolvedSelection.includes(option.name);
                const checkboxColor = option.color || 'var(--color-chart-title, #33485e)';
                return (
                  <React.Fragment key={option.dividerBefore ? `divider-${option.name}` : option.name}>
                    {option.dividerBefore && (
                      <MenuItem
                        disabled
                        disableRipple
                        disableGutters
                        dense
                        value={`${LEGEND_SEPARATOR_PREFIX}${option.name}`}
                        className="series-controls-legend-divider"
                        sx={{
                          width: '100%',
                          opacity: 1,
                          pointerEvents: 'none',
                          '&.Mui-disabled': { opacity: 1 },
                        }}
                      >
                        <Box
                          className="series-controls-legend-divider-line"
                          sx={{ width: '100%', height: '1px', backgroundColor: '#99a3af', my: '7px' }}
                        />
                      </MenuItem>
                    )}
                    <MenuItem
                      value={option.name}
                      sx={{ pl: 0, pr: '6px', width: '100%' }}
                      onClick={event => handleSeriesMenuItemClick(option.name, event)}
                    >
                      <Checkbox
                        sx={{ color: checkboxColor, '&.Mui-checked': { color: checkboxColor } }}
                        checked={isChecked}
                        tabIndex={-1}
                        disableRipple
                      />
                      {renderSeriesOptionTooltip(option, <ListItemText primary={option.name} />)}
                    </MenuItem>
                  </React.Fragment>
                );
              })}
            </MuiSelect>
          </SeriesSelectControl>
        </ControlsRow>
      )}
      {showBottomRowMean && (
        <ControlsRow className="sma-row">
          <MeanToggleLabel $issolo data-disabled={meanDisabled ? 1 : 0}>
            <span>{meanLabel}</span>
            <MeanToggleInput
              type="checkbox"
              checked={meanChecked}
              onChange={handleMeanToggle}
              disabled={meanDisabled}
            />
          </MeanToggleLabel>
        </ControlsRow>
      )}
    </Container>
  );
};

export default SeriesControls;
